import { useGetApInvoiceTransactionGeneralInputOptions } from '@/context/services/transaction_items/Detail/TransactionItemDetail.service';
import { ApGeneralInputOption } from 'ap-openapi';
import { useCallback } from 'react';

export const optionsCache = new Map<string, ApGeneralInputOption[]>();
const loadingPromises = new Map<string, Promise<ApGeneralInputOption[]>>();

export const useFindPulldownOption = () => {
  const getApInvoiceTransactionGeneralInputOptions =
    useGetApInvoiceTransactionGeneralInputOptions();

  const getAllOptions = useCallback(
    async (
      propertyId: string,
      keyword: keyof ApGeneralInputOption,
      value: string
    ): Promise<ApGeneralInputOption[]> => {
      const cachedOptions = optionsCache.get(propertyId);

      if (loadingPromises.has(propertyId)) {
        const existingPromise = loadingPromises.get(propertyId)!;
        const existingResult = await existingPromise;
        const found = existingResult.find((item) => item[keyword] === value);
        if (found) {
          return existingResult;
        }
      }

      const loadingPromise = (async () => {
        try {
          const per = 100;
          let page = cachedOptions?.length
            ? Math.ceil(cachedOptions.length / per) + 1
            : 1;
          let hasMore = true;
          const allItems: ApGeneralInputOption[] = [...(cachedOptions ?? [])];

          while (hasMore) {
            const response = await getApInvoiceTransactionGeneralInputOptions(
              propertyId,
              {
                keyword: '',
                page,
                per: per,
              }
            );
            const items = response.data?.ap_general_input_options ?? [];
            allItems.push(...items);

            const found = items.find((item) => item[keyword] === value);
            if (found) {
              hasMore = false;
            } else {
              hasMore = items.length === per;
            }
            page++;
          }

          optionsCache.set(propertyId, allItems);
          return allItems;
        } finally {
          loadingPromises.delete(propertyId);
        }
      })();

      loadingPromises.set(propertyId, loadingPromise);
      return loadingPromise;
    },
    [getApInvoiceTransactionGeneralInputOptions]
  );

  const findOption = useCallback(
    async <T,>(
      propertyId: string,
      keyword: keyof ApGeneralInputOption,
      value: string,
      finder: (item: ApGeneralInputOption) => boolean,
      transformer: (option: ApGeneralInputOption) => T
    ): Promise<T | undefined> => {
      const cachedOptions = optionsCache.get(propertyId);
      if (cachedOptions) {
        const option = cachedOptions.find(finder);
        if (option) {
          return transformer(option);
        }
      }

      if (loadingPromises.has(propertyId)) {
        const existingResult = await loadingPromises.get(propertyId)!;
        const option = existingResult.find(finder);
        if (option) {
          return transformer(option);
        }
      }

      const allOptions = await getAllOptions(propertyId, keyword, value);
      const option = allOptions.find(finder);
      return option ? transformer(option) : undefined;
    },
    [getAllOptions]
  );

  const findByLabel = useCallback(
    async (propertyId: string, label: string): Promise<string> => {
      const result = await findOption(
        propertyId,
        'label',
        label,
        (item) => item.label === label,
        (option) => option.id ?? ''
      );
      return result ?? '';
    },
    [findOption]
  );

  const findById = useCallback(
    async (propertyId: string, id: string): Promise<string> => {
      const result = await findOption(
        propertyId,
        'id',
        id,
        (item) => item.id === id,
        (option) =>
          option.label?.concat(option.code ? `（${option.code}）` : '') ?? ''
      );
      return result ?? '';
    },
    [findOption]
  );

  return {
    findPulldownOptionByLabel: findByLabel,
    findPulldownOptionById: findById,
  };
};
